import React from 'react';
import Table from '../../components/common/Table';
import { CompetitionDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { Link } from 'lucide-react';
import Badge from '../common/Badge';
import { renderBooleanBadge } from '../../utils/displayUtils';

interface CompetitionTableProps {
  data: CompetitionDTO[];
  handleEdit: (row: CompetitionDTO) => void;
  handleDelete: (row: CompetitionDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const CompetitionTable: React.FC<CompetitionTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => {
  return (
    <Table
      columns={[
        {
          label: 'Name',
          key: 'name',
          sortable: true,
          sortKey: 'Name',
        },

        {
          label: 'Registration Open',
          key: 'registrationOpenDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },

        {
          label: 'Registration Close',
          key: 'registrationCloseDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },

        {
          label: 'Competition Start',
          key: 'startDate',
          sortable: true,
          sortKey: 'StartDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },

        {
          label: 'Competition End',
          key: 'endDate',
          sortable: true,
          sortKey: 'EndDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },

        {
          label: 'Amount',
          key: 'amount',
        },

        {
          label: 'Rules',
          key: 'rulesTargetUrl',
          render: (row: CompetitionDTO) => {
            return row.rulesTargetUrl ? (
              <a href={row.rulesTargetUrl} target='_blank' className="cursor-pointer">
                <Link size={15} />
              </a>
            ) : (
              <span>-</span>
            );
          },
        },

        {
          label: 'Active',
          key: 'isActive',
          render: (row: CompetitionDTO) => {
            const openDate = new Date(row.registrationOpenDate ?? '').getTime();
            return renderBooleanBadge(openDate < Date.now(), 'Active', 'Inactive');
          }
        },

        {
          label: 'Added',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default CompetitionTable;
